import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormCreator, FormCreatorProps, FormFieldDef } from "@/components/shared/form-creator";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet.tsx";

const schema = z.object({
  email: z.string().trim().email("Enter the email they signed up with."),
  role: z.string().optional(),
  notes: z.string().optional(),
  is_primary_contact: z.boolean(),
});

export type AddContactForm = z.infer<typeof schema>;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: AddContactForm) => Promise<boolean>;
};

const fields: FormFieldDef[] = [
  { type: "text", name: "email", label: "Email", required: true },
  { type: "text", name: "role", label: "Role" },
  { type: "textarea", name: "notes", label: "Notes" },
  { type: "switch", name: "is_primary_contact", label: "Primary Contact", wrapperClassName: "flex items-center justify-between mt-4" },
];

export function AddContactModal({ open, onOpenChange, onSubmit }: Props) {
  const form = useForm<AddContactForm>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", role: "", notes: "", is_primary_contact: false },
  });

  const props: FormCreatorProps = {
    form,
    onSubmit: form.handleSubmit(async (values) => {
      if (await onSubmit(values)) form.reset();
    }),
    formDefinition: fields,
    buttonText: "Add Contact",
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Add a Contact to the Property</SheetTitle>
          <SheetDescription>
            Anyone with a Shyne account, from any company, can be a contact. They see this property and its appointments, read-only.
          </SheetDescription>
          <FormCreator {...props} />
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
}
